
"use client";

import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Send, ArrowRight, MoreVertical } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSearchParams } from 'next/navigation';

const MOCK_CHATS = [
  { id: 'chat_1', name: 'أحمد علي', lastMsg: 'هل السعر قابل للتفاوض؟', time: '10:30 ص', unread: 2, avatar: 'https://picsum.photos/seed/user1/100/100' },
  { id: 'chat_2', name: 'معرض النخبة', lastMsg: 'تم فحص السيارة وهي جاهزة للتسليم', time: 'أمس', unread: 0, avatar: 'https://picsum.photos/seed/user2/100/100' },
  { id: 'chat_3', name: 'سارة محمد', lastMsg: 'شكراً جزيلاً لك', time: 'قبل يومين', unread: 0, avatar: 'https://picsum.photos/seed/user3/100/100' },
];

const MOCK_MESSAGES = [
  { id: 1, text: 'السلام عليكم، هل السيارة لا تزال موجودة؟', sender: 'other', time: '10:00 ص' },
  { id: 2, text: 'وعليكم السلام، نعم موجودة وبحالة ممتازة.', sender: 'me', time: '10:05 ص' },
  { id: 3, text: 'هل السعر قابل للتفاوض؟', sender: 'other', time: '10:30 ص' },
];

import { Suspense } from 'react';

function ChatContent() {
  const searchParams = useSearchParams();
  const activeChatId = searchParams.get('id');
  const queryName = searchParams.get('name');
  const queryListing = searchParams.get('listing');
  const [selectedChat, setSelectedChat] = useState<any>(null);
  const [msgInput, setMsgInput] = useState('');
  const [messages, setMessages] = useState(MOCK_MESSAGES);

  const queryChat = activeChatId && !MOCK_CHATS.some(c => c.id === activeChatId)
    ? {
      id: activeChatId,
      name: queryName || 'معلن جديد',
      lastMsg: queryListing ? `استفسار بخصوص: ${queryListing}` : 'بدء محادثة جديدة',
      time: 'الآن',
      unread: 0,
      avatar: `https://picsum.photos/seed/${encodeURIComponent(activeChatId)}/100/100`,
    }
    : null;
  const chatList = queryChat ? [queryChat, ...MOCK_CHATS] : MOCK_CHATS;

  useEffect(() => {
    if (activeChatId) {
      const chat = chatList.find(c => c.id === activeChatId);
      if (chat) {
        setSelectedChat(chat);
        if (queryListing) {
          setMessages([
            {
              id: Date.now(),
              text: `مرحباً، أنا مهتم بإعلان "${queryListing}".`,
              sender: 'me',
              time: new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' }),
            } as any,
          ]);
        }
      }
    }
  }, [activeChatId, queryListing]);

  const handleSendMessage = () => {
    if (!msgInput.trim()) return;
    const newMsg = {
      id: Date.now(),
      text: msgInput,
      sender: 'me',
      time: new Date().toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })
    };
    setMessages([...messages, newMsg]);
    setMsgInput('');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col h-screen overflow-hidden">
      {!selectedChat ? (
        <>
          <Navigation title="المحادثات" />
          <div className="p-4 space-y-4 flex-1 overflow-y-auto">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input className="pr-10 rounded-2xl bg-white border-none shadow-sm h-12" placeholder="بحث في المحادثات..." />
            </div>

            <div className="space-y-2">
              {chatList.map((chat) => (
                <Card
                  key={chat.id}
                  className="border-none shadow-sm hover:shadow-md transition-shadow rounded-2xl cursor-pointer bg-white"
                  onClick={() => setSelectedChat(chat)}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <Avatar className="h-14 w-14 border">
                      <AvatarImage src={chat.avatar} />
                      <AvatarFallback>{chat.name[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="font-bold text-lg truncate">{chat.name}</h3>
                        <span className="text-xs text-muted-foreground">{chat.time}</span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{chat.lastMsg}</p>
                    </div>
                    {chat.unread > 0 && (
                      <div className="bg-primary text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center">
                        {chat.unread}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col h-full bg-white">
          {/* Chat Header */}
          <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setSelectedChat(null)} className="rounded-full">
                <ArrowRight className="h-6 w-6" />
              </Button>
              <Avatar className="h-10 w-10">
                <AvatarImage src={selectedChat.avatar} />
                <AvatarFallback>{selectedChat.name[0]}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-bold text-sm leading-none">{selectedChat.name}</h3>
                <span className="text-[10px] text-green-500 font-bold">متصل الآن</span>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="rounded-full">
              <MoreVertical className="h-5 w-5 text-slate-400" />
            </Button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F8FAFC]">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'me' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${msg.sender === 'me'
                    ? 'bg-primary text-white rounded-tr-none'
                    : 'bg-white text-slate-800 rounded-tl-none'
                  }`}>
                  <p className="text-sm leading-relaxed">{msg.text}</p>
                  <span className={`text-[9px] block mt-1 ${msg.sender === 'me' ? 'text-white/70' : 'text-slate-400'}`}>
                    {msg.time}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t flex gap-2 items-center">
            <Input
              className="flex-1 h-12 rounded-2xl bg-slate-50 border-none px-4"
              placeholder="اكتب رسالتك هنا..."
              value={msgInput}
              onChange={(e) => setMsgInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            <Button
              className="h-12 w-12 rounded-2xl bg-primary text-white shadow-lg"
              onClick={handleSendMessage}
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ChatPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center font-bold text-primary">جاري تحميل المحادثات...</div>}>
      <ChatContent />
    </Suspense>
  );
}
